import { Tabs, Typography } from "antd";
import { createContext, useContext, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Term } from "./Term";

/** Glossary terms of the current page, provided by the shell from its route registry. */
export const PageTermsContext = createContext<readonly string[]>([]);

export interface PageHeaderTab {
  key: string;
  label: ReactNode;
}

export interface PageHeaderProps {
  title: ReactNode;
  /** One plain line: what this page is for / what you do here. */
  purpose?: ReactNode;
  /** @deprecated alias of `purpose` (kept so existing pages need no change). */
  subtitle?: ReactNode;
  /** Primary action(s), shown on the end side. */
  actions?: ReactNode;
  /** @deprecated alias of `actions`. */
  extra?: ReactNode;
  /** Optional page-level tabs. */
  tabs?: PageHeaderTab[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  /** Glossary ids explained under the purpose line (added to the route's terms). */
  terms?: readonly string[];
}

/** Every page starts with this header (ADR-0055): title, purpose line, actions, key terms, tabs. */
export function PageHeader({ title, purpose, subtitle, actions, extra, tabs, activeTab, onTabChange, terms }: PageHeaderProps) {
  const { t } = useTranslation("glossary");
  const fromRoute = useContext(PageTermsContext);
  const allTerms = [...new Set([...(terms ?? []), ...fromRoute])];
  const lead = purpose ?? subtitle;
  const end = actions ?? extra;
  return (
    <header className="ap-page-header">
      <div className="ap-page-header-row">
        <div className="ap-page-header-text">
          <Typography.Title level={1} className="ap-page-title">
            {title}
          </Typography.Title>
          {lead ? <p className="ap-page-purpose">{lead}</p> : null}
          {allTerms.length > 0 ? (
            <p className="ap-page-terms" data-testid="page-terms">
              <span className="ap-page-terms-label">{t("ui.keyTerms")}</span>
              {allTerms.map((id) => (
                <Term key={id} id={id} />
              ))}
            </p>
          ) : null}
        </div>
        {end ? <div className="ap-page-actions">{end}</div> : null}
      </div>
      {tabs && tabs.length > 0 ? (
        <Tabs className="ap-page-tabs" activeKey={activeTab} onChange={onTabChange} items={tabs.map((x) => ({ key: x.key, label: x.label }))} />
      ) : null}
    </header>
  );
}
